import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";

import {
  getFreightAnalysis,
  getFreightRequestList,
} from "../../../api/director/navlunApi";

import { formatDate, formatPrice } from "./navlunHelpers";

function DirectorNavlunAnalysisPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [analysis, setAnalysis] = useState(null);

  const [analysisFilter, setAnalysisFilter] = useState({
    requestId: "",
    currency: "EUR",
  });

  const fetchAnalysis = useCallback(
    async (
      currencyValue = analysisFilter.currency,
      requestIdValue = analysisFilter.requestId
    ) => {
      try {
        setLoading(true);

        const response = await getFreightAnalysis(
          1,
          200,
          currencyValue,
          requestIdValue
        );

        if (response?.data?.success) {
          setAnalysis(response.data.data);
        }
      } catch (error) {
        console.error("Navlun analiz verileri alınırken hata:", error);
        alert("Navlun analiz verileri alınamadı.");
      } finally {
        setLoading(false);
      }
    },
    [analysisFilter.currency, analysisFilter.requestId]
  );

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);

      const requestResponse = await getFreightRequestList(1, 200);

      if (requestResponse?.data?.success) {
        const requestList = requestResponse.data.data || [];
        setRequests(requestList);

        if (requestList.length > 0) {
          const firstRequest = requestList[0];

          const firstFilter = {
            requestId: firstRequest._id,
            currency: firstRequest.currency || "EUR",
          };

          setAnalysisFilter(firstFilter);

          await fetchAnalysis(firstFilter.currency, firstFilter.requestId);
        } else {
          await fetchAnalysis("EUR", "");
        }
      }
    } catch (error) {
      console.error("Navlun analiz başlangıç verileri alınırken hata:", error);
      alert("Navlun analiz verileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }, [fetchAnalysis]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleRequestChange = async (e) => {
    const requestId = e.target.value;

    const selectedRequest = requests.find((item) => item._id === requestId);

    const nextCurrency =
      selectedRequest?.currency || analysisFilter.currency || "EUR";

    const nextFilter = {
      requestId,
      currency: nextCurrency,
    };

    setAnalysisFilter(nextFilter);

    await fetchAnalysis(nextFilter.currency, nextFilter.requestId);
  };

  const handleAnalysisCurrencyChange = async (e) => {
    const value = e.target.value;

    const nextFilter = {
      ...analysisFilter,
      currency: value,
    };

    setAnalysisFilter(nextFilter);

    await fetchAnalysis(nextFilter.currency, nextFilter.requestId);
  };

  const getNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number : 0;
  };

  const getCompanyName = (offer) => {
    return offer?.companyId?.companyName || offer?.companyName || "-";
  };

  const truncateText = (value, max = 60) => {
    if (!value) return "-";

    const text = String(value);

    if (text.length <= max) return text;

    return `${text.slice(0, max)}...`;
  };

  const selectedRequest = requests.find(
    (item) => item._id === analysisFilter.requestId
  );

  const summary = analysis?.summary || {};

  const offers = useMemo(() => {
    return analysis?.offers || [];
  }, [analysis?.offers]);

  const activeCurrency =
    analysis?.filters?.currency || analysisFilter.currency || "EUR";

  const sortedOffers = useMemo(() => {
    return [...offers].sort((a, b) => {
      return getNumber(a.totalPrice) - getNumber(b.totalPrice);
    });
  }, [offers]);

  const minTotalOffer = sortedOffers.length > 0 ? sortedOffers[0] : null;

  const maxTotalOffer =
    sortedOffers.length > 0 ? sortedOffers[sortedOffers.length - 1] : null;

  const minTotalPrice = getNumber(minTotalOffer?.totalPrice);

  const getDiffPercent = (totalPrice) => {
    const price = getNumber(totalPrice);

    if (!minTotalPrice || !price) return 0;

    return ((price - minTotalPrice) / minTotalPrice) * 100;
  };

  const getLowestByField = (field) => {
    const validOffers = sortedOffers.filter(
      (offer) => getNumber(offer[field]) > 0
    );

    if (!validOffers.length) return null;

    return validOffers.reduce((min, offer) => {
      return getNumber(offer[field]) < getNumber(min[field]) ? offer : min;
    });
  };

  const lowestRows = [
    {
      label: "Navlun",
      field: "freightPrice",
    },
    {
      label: "Toplam",
      field: "totalPrice",
    },
  ].map((item) => {
    const offer = getLowestByField(item.field);

    return {
      ...item,
      offer,
    };
  });

  if (!user) return null;

  return (
    <Layout>
      <div className="content-body" style={{ width: "100%", padding: "20px" }}>
        <div className="row mt-3 mb-4">
          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#e6ffed",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#16803c", fontWeight: "600" }}>
                  GEÇERLİ TEKLİF
                </h6>
                <h2
                  style={{
                    color: "#16803c",
                    fontSize: "2.2rem",
                    margin: "10px 0",
                  }}
                >
                  {summary.validOfferCount || 0}
                </h2>
                <small style={{ color: "#16803c" }}>
                  Sadece eşleşen firmalar
                </small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#e8f4ff",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#0066cc", fontWeight: "600" }}>
                  EN UYGUN TEKLİF
                </h6>
                <h2
                  style={{
                    color: "#0066cc",
                    fontSize: "1.35rem",
                    margin: "14px 0 6px",
                  }}
                >
                  {minTotalOffer
                    ? formatPrice(
                        minTotalOffer.totalPrice,
                        minTotalOffer.currency
                      )
                    : formatPrice(0, activeCurrency)}
                </h2>
                <small style={{ color: "#0066cc" }}>
                  {minTotalOffer ? getCompanyName(minTotalOffer) : "-"}
                </small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#ffe6cc",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#ff6600", fontWeight: "600" }}>
                  EN YÜKSEK TEKLİF
                </h6>
                <h2
                  style={{
                    color: "#ff6600",
                    fontSize: "1.35rem",
                    margin: "14px 0 6px",
                  }}
                >
                  {maxTotalOffer
                    ? formatPrice(
                        maxTotalOffer.totalPrice,
                        maxTotalOffer.currency
                      )
                    : formatPrice(0, activeCurrency)}
                </h2>
                <small style={{ color: "#ff6600" }}>
                  {maxTotalOffer ? getCompanyName(maxTotalOffer) : "-"}
                </small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#f1e8ff",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#6633cc", fontWeight: "600" }}>
                  ORTALAMA
                </h6>
                <h2
                  style={{
                    color: "#6633cc",
                    fontSize: "1.35rem",
                    margin: "14px 0",
                  }}
                >
                  {formatPrice(summary.avgPrice || 0, activeCurrency)}
                </h2>
                <small style={{ color: "#6633cc" }}>
                  Seçili para birimi: {activeCurrency}
                </small>
              </div>
            </div>
          </div>
        </div>

        <section className="app-user-list">
          <div className="row">
            <div className="col-12">
              <div
                className="card mb-4"
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-header border-bottom p-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title m-0">Navlun Teklif Analizi</h4>
                    <small className="text-muted">
                      Seçilen teklif talebine bağlı gelen firma tekliflerini
                      karşılaştırır.
                    </small>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-control form-control-sm"
                      style={{ minWidth: "320px" }}
                      value={analysisFilter.requestId}
                      onChange={handleRequestChange}
                    >
                      <option value="">Teklif talebi seçiniz</option>
                      {requests.map((request) => (
                        <option key={request._id} value={request._id}>
                          {request.requestCode} - {request.routeFrom} →{" "}
                          {request.routeTo} - {request.currency}
                        </option>
                      ))}
                    </select>

                    <select
                      className="form-control form-control-sm"
                      style={{ width: "100px" }}
                      value={analysisFilter.currency}
                      onChange={handleAnalysisCurrencyChange}
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="TRY">TRY</option>
                    </select>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() =>
                        fetchAnalysis(
                          analysisFilter.currency,
                          analysisFilter.requestId
                        )
                      }
                      disabled={loading}
                    >
                      Yenile
                    </button>
                  </div>
                </div>

                <div className="card-body pt-3">
                  {loading && (
                    <div className="alert alert-info py-2">
                      İşlem yapılıyor...
                    </div>
                  )}

                  {selectedRequest && (
                    <div className="alert alert-light border py-2">
                      <strong>Seçili Talep:</strong>{" "}
                      {selectedRequest.requestCode} - {selectedRequest.title} |{" "}
                      <strong>Rota:</strong> {selectedRequest.routeFrom} →{" "}
                      {selectedRequest.routeTo} | <strong>Para Birimi:</strong>{" "}
                      {selectedRequest.currency}
                    </div>
                  )}

                  <div className="alert alert-warning py-2">
                    <strong>Bilgi:</strong> Eşleşmeyen cevaplar ana fiyat
                    karşılaştırmasına dahil edilmez. Analiz sadece seçilen
                    teklif talebine bağlı cevaplar üzerinden yapılır.
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Sıra</th>
                          <th>Teklif Kodu</th>
                          <th>Firma</th>
                          <th>Rota</th>
                          <th>Navlun</th>
                          <th>Line</th>
                          <th>Freetime</th>
                          <th>Teslim TT</th>
                          <th>Toplam</th>
                          <th>Fark %</th>
                          <th>Teslim</th>
                          <th>Geçerlilik</th>
                          <th>Not</th>
                        </tr>
                      </thead>

                      <tbody>
                        {sortedOffers.length > 0 ? (
                          sortedOffers.map((offer, index) => {
                            const diffPercent = getDiffPercent(
                              offer.totalPrice
                            );

                            const isBest = index === 0;

                            return (
                              <tr
                                key={offer._id}
                                style={{
                                  backgroundColor: isBest
                                    ? "#f0fff4"
                                    : "transparent",
                                }}
                              >
                                <td>
                                  {isBest ? (
                                    <span className="badge bg-success">
                                      En Uygun
                                    </span>
                                  ) : (
                                    index + 1
                                  )}
                                </td>

                                <td>{offer.requestId?.requestCode || "-"}</td>

                                <td className="fw-bold">
                                  {getCompanyName(offer)}
                                </td>

                                <td>
                                  {offer.requestId
                                    ? `${offer.requestId.routeFrom} → ${offer.requestId.routeTo}`
                                    : "-"}
                                </td>

                                <td>
                                  {formatPrice(
                                    offer.freightPrice,
                                    offer.currency
                                  )}
                                </td>

                                <td>{offer.line || "-"}</td>

                                <td>{offer.freeTime || "-"}</td>

                                <td>{offer.deliveryTransitTime || "-"}</td>

                                <td className="fw-bold">
                                  {formatPrice(
                                    offer.totalPrice,
                                    offer.currency
                                  )}
                                </td>

                                <td>
                                  {isBest ? (
                                    <span className="badge bg-success">
                                      Referans
                                    </span>
                                  ) : (
                                    <span className="badge bg-warning">
                                      +{diffPercent.toFixed(2)}%
                                    </span>
                                  )}
                                </td>

                                <td>
                                  {offer.deliveryDay
                                    ? `${offer.deliveryDay} gün`
                                    : "-"}
                                </td>

                                <td>{formatDate(offer.validityDate)}</td>

                                <td title={offer.note || ""}>
                                  {truncateText(offer.note, 50)}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="13" className="text-center p-4">
                              Seçili teklif talebi ve para birimi için eşleşen
                              teklif bulunamadı.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="alert alert-secondary mt-3 mb-0">
                    Aktif para birimi: <strong>{activeCurrency}</strong>
                  </div>
                </div>
              </div>

              <div
                className="card"
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-header border-bottom p-4">
                  <h4 className="card-title m-0">Fiyat Bazlı En Düşükler</h4>
                  <small className="text-muted">
                    Seçilen teklif talebinde navlun ve toplam fiyat için en
                    düşük teklifi veren firma.
                  </small>
                </div>

                <div className="card-body pt-3">
                  <div className="table-responsive">
                    <table className="table table-hover table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Kalem</th>
                          <th>En Düşük Firma</th>
                          <th>Tutar</th>
                          <th>Teklif Kodu</th>
                          <th>Rota</th>
                          <th>Line</th>
                          <th>Freetime</th>
                          <th>Teslim TT</th>
                        </tr>
                      </thead>

                      <tbody>
                        {lowestRows.length > 0 ? (
                          lowestRows.map((row) => (
                            <tr key={row.field}>
                              <td className="fw-bold">{row.label}</td>

                              <td>
                                {row.offer ? getCompanyName(row.offer) : "-"}
                              </td>

                              <td>
                                {row.offer
                                  ? formatPrice(
                                      row.offer[row.field],
                                      row.offer.currency
                                    )
                                  : formatPrice(0, activeCurrency)}
                              </td>

                              <td>
                                {row.offer?.requestId?.requestCode || "-"}
                              </td>

                              <td>
                                {row.offer?.requestId
                                  ? `${row.offer.requestId.routeFrom} → ${row.offer.requestId.routeTo}`
                                  : "-"}
                              </td>

                              <td>{row.offer?.line || "-"}</td>

                              <td>{row.offer?.freeTime || "-"}</td>

                              <td>{row.offer?.deliveryTransitTime || "-"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center p-4">
                              Fiyat bazlı analiz verisi bulunamadı.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="alert alert-secondary mt-3 mb-0">
                    Form seçimi analiz ekranında yapılır. Google Form sadece
                    veri toplama aracıdır; karşılaştırma ve karar analizi bu
                    sayfada yapılır. Line, freetime ve teslim TT bilgileri karar
                    destek alanlarıdır; fiyat kalemi olarak hesaplanmaz.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default DirectorNavlunAnalysisPage;
